import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { checkIfOffRoute, calculateDistance } from '../logic/NavigationHelpers';
import { addToast } from '../../ui/toastSlice';
import { useTranslation } from 'react-i18next';
import {
    setCurrentLocation,
    setCurrentBearing,
    setCurrentSpeed,
    selectRouteCoordinates,
    selectIsDrivingMode,
    selectIsRerouting,
    setIsDrivingMode,
    setIsRoutingMode,
} from '../mapSlice';
import { useRerouting } from './useRerouting';

/**
 * Custom hook to manage real GPS tracking for production navigation
 * Uses browser's Geolocation API to track user's actual location
 */
export const useGPSTracking = (checkProximity) => {
    const dispatch = useDispatch();
    const routeCoordinates = useSelector(selectRouteCoordinates);
    const isDrivingMode = useSelector(selectIsDrivingMode);
    const isRerouting = useSelector(selectIsRerouting);
    const { t } = useTranslation();

    const watchIdRef = useRef(null);
    const lastPositionRef = useRef(null);
    const arrivalTriggeredRef = useRef(false);

    const { updateDestination, triggerReroute } = useRerouting();

    const calculateBearing = (lat1, lon1, lat2, lon2) => {
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const y = Math.sin(dLon) * Math.cos(lat2 * Math.PI / 180);
        const x = Math.cos(lat1 * Math.PI / 180) * Math.sin(lat2 * Math.PI / 180) -
            Math.sin(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.cos(dLon);
        const bearing = Math.atan2(y, x) * 180 / Math.PI;
        return (bearing + 360) % 360;
    };

    const startGPSTracking = () => {
        if (!routeCoordinates || !navigator.geolocation) return;

        // Store destination for rerouting
        updateDestination();

        // Reset arrival flag
        arrivalTriggeredRef.current = false;

        // Start watching position
        watchIdRef.current = navigator.geolocation.watchPosition(
            (position) => {
                const { latitude, longitude, speed: gpsSpeed, heading } = position.coords;
                const currentLoc = { lat: latitude, lng: longitude };

                // Calculate bearing from movement if heading not available
                let bearing = heading;
                if (bearing === null && lastPositionRef.current) {
                    bearing = calculateBearing(
                        lastPositionRef.current.lat,
                        lastPositionRef.current.lng,
                        latitude,
                        longitude
                    );
                } else if (bearing === null) {
                    bearing = 0; // Default if no previous position
                }

                // Convert speed from m/s to km/h (if available)
                const speedKmh = gpsSpeed !== null ? gpsSpeed * 3.6 : 0;

                // Update state
                dispatch(setCurrentLocation(currentLoc));
                dispatch(setCurrentBearing(bearing));
                dispatch(setCurrentSpeed(speedKmh));
                checkProximity(currentLoc, bearing);

                // Check for arrival (within 30 meters of destination)
                if (routeCoordinates && routeCoordinates.length > 0) {
                    const destination = routeCoordinates[routeCoordinates.length - 1];
                    const distanceToDest = calculateDistance(
                        currentLoc.lat,
                        currentLoc.lng,
                        destination.lat,
                        destination.lng
                    );

                    if (distanceToDest < 20) {
                        if (arrivalTriggeredRef.current) return;

                        arrivalTriggeredRef.current = true;
                        dispatch(addToast({
                            message: t('hud.arrived'),
                            type: 'info',
                            duration: 5000
                        }));
                        dispatch(setIsDrivingMode(false));
                        dispatch(setIsRoutingMode(false));
                        return;
                    }
                }

                // Check if off route and trigger reroute
                if (!isRerouting && checkIfOffRoute(currentLoc, routeCoordinates)) {
                    triggerReroute(currentLoc, stopGPSTracking);
                }

                lastPositionRef.current = currentLoc;
            },
            (error) => {
                console.error('GPS tracking error:', error);
            },
            {
                enableHighAccuracy: true,
                timeout: 5000,
                maximumAge: 0
            }
        );
    };

    const stopGPSTracking = () => {
        if (watchIdRef.current !== null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
            lastPositionRef.current = null;
        }
    };

    // Auto-start/stop GPS tracking based on driving mode
    useEffect(() => {
        if (isDrivingMode && routeCoordinates) {
            startGPSTracking();
        } else {
            stopGPSTracking();
        }

        return () => stopGPSTracking();
    }, [isDrivingMode, routeCoordinates]); // eslint-disable-line react-hooks/exhaustive-deps

    // Restart GPS tracking after rerouting
    useEffect(() => {
        if (isRerouting && routeCoordinates && isDrivingMode) {
            // New route found, restart GPS tracking
            stopGPSTracking();
            startGPSTracking();
        }
    }, [routeCoordinates]); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        startGPSTracking,
        stopGPSTracking
    };
};
